import React, { useEffect, useMemo } from 'react';
import { useMeasure } from 'react-use';

import useScreen from '@lyearn/core/hooks/useScreen';
import { jsonParse } from '@lyearn/core/utils/json';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import Breadcrumbs from '@/components/Breadcrumb/components/Breadcrumbs';
import { useFetchConfig } from '@/components/ConfigsProvider';
import { NavigationCenterTabs } from '@/components/Header/types';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useScrollProgress from '@/hooks/useScrollProgress';
import useDefaultWidgetId from '@/modules/corporate/modules/discover/views/Discover/hooks/useDefaultWidgetId';

import DefaultLeftNode from '../components/DefaultLeftNode';
import DefaultRightNodeLazy from '../components/DefaultRightNode/index.lazy';
import { useHeaderContext } from '../context/HeaderContext';
import { DefaultHeaderProps } from '../types';
import DefaultHeaderLegacy from './index.desktop.legacy';
import { headerOnBg } from './style';

const DefaultHeader = React.memo(
  (
    props: DefaultHeaderProps & {
      animate: boolean;
    },
  ) => {
    const {
      LeftNode,
      RightNode,
      className,
      showNavigationTabs,
      CenterNode: CenterNodeProp,
      SubHeader,
      logoPreviewUrl,
      animate,
      isDisabled,
      showBreadcrumbs,
      previewNavConfig,
    } = props;

    const [headerRef, { height: computedHeight }] = useMeasure<HTMLDivElement>();
    const { isSearchOpen } = useHeaderContext();

    const { defaultWidgetId } = useDefaultWidgetId();
    const { [FeatureFlags.EnableUnifiedDashboard]: isUnifiedDashboardEnabled } = useFeatureFlags();

    // Fetch unified nav config for center node when unified dashboard is enabled
    const configToFetch = isUnifiedDashboardEnabled ? 'unifiedNavConfig' : 'navConfig';
    const configs = useFetchConfig([configToFetch]);
    const navConfig = isUnifiedDashboardEnabled ? configs.unifiedNavConfig : configs.navConfig;

    // Parse center node config from unified nav config
    const centerNodeConfig = useMemo(() => {
      if (!isUnifiedDashboardEnabled) {
        return null;
      }

      // Use previewNavConfig if available, otherwise use fetched navConfig
      const configToUse = previewNavConfig || navConfig;

      if (!(configToUse as any)?.centerNodeConfig) {
        return null;
      }

      try {
        const parsed = jsonParse<{
          enabled?: boolean;
          activeTab?: NavigationCenterTabs;
        }>((configToUse as any).centerNodeConfig);
        return parsed.data;
      } catch {
        return null;
      }
    }, [isUnifiedDashboardEnabled, previewNavConfig, navConfig]);

    useEffect(() => {
      document.body.style.setProperty('--ly-header-height', `${computedHeight}px`);
      return () => {
        document.body.style.removeProperty('--ly-header-height');
      };
    }, [computedHeight]);

    const HeaderLeftNode =
      showNavigationTabs || LeftNode === undefined ? (
        <DefaultLeftNode logoPreviewUrl={logoPreviewUrl} textColor="text-text-primary" />
      ) : (
        LeftNode
      );

    const HeaderRightNode =
      showNavigationTabs || RightNode === undefined ? (
        <DefaultRightNodeLazy previewNavConfig={previewNavConfig} />
      ) : (
        RightNode
      );
    const { isLandScapeMode } = useScreen();
    const height = isLandScapeMode ? 'h-[48px]' : 'h-[52px]';
    const SubHeaderNode = showNavigationTabs || SubHeader === undefined ? null : SubHeader;

    return (
      <Stack
        ref={headerRef}
        className={cn(
          'sticky top-0 z-50 border-b border-border',
          animate ? 'animate' : '',
          headerOnBg(showNavigationTabs),
          isDisabled ? 'pointer-events-none' : '',
        )}
        component="header"
        direction="col">
        <Box
          className={cn(
            'relative flex flex-shrink-0 items-center border-divider bg-bg-primary px-8 py-10 text-text-primary',
            className,
            height,
            { 'border-b': !showNavigationTabs },
          )}>
          <Box className="flex flex-row items-center">{HeaderLeftNode}</Box>
          {CenterNodeProp ? (
            <Box className="-mb-10 ms-30">
              {React.cloneElement(CenterNodeProp as React.ReactElement<any>, {
                navConfig: previewNavConfig || navConfig,
              })}
            </Box>
          ) : null}
          <Box className="flex flex-grow flex-row justify-end me-12">{HeaderRightNode}</Box>
        </Box>
        {SubHeaderNode}
        {showBreadcrumbs ? <Breadcrumbs className={animate ? undefined : 'border-b'} /> : null}
      </Stack>
    );
  },
);

function DefaultHeaderPreUnified(props: DefaultHeaderProps) {
  const { scrollProgress } = useScrollProgress(props.scrollContainerEl);
  const animate = !!scrollProgress;
  const { [FeatureFlags.EnableUnifiedDashboard]: isUnifiedDashboardEnabled } = useFeatureFlags();

  if (isUnifiedDashboardEnabled) {
    return <DefaultHeader animate={animate} {...props} />;
  } else {
    return <DefaultHeaderLegacy animate={animate} {...props} />;
  }
}

export default DefaultHeaderPreUnified;
